import {
  Issue as DomainIssue,
  Message,
  NewMessage,
  cacheKeys,
  useCreateIssueMessage,
  useCurrentWorkspace,
  useFindUnreadActivityInbox,
  useIssue,
  useSetActivityInboxRead,
} from '@gripp/shared-logic';
import { Colors, MessageThread, tokens } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { ElementRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { getMediaBaseUrl } from '../../shared/mediaUtils';
import type { CampaignResponseRow } from './campaignResponsesPanel';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CampaignThreadPanelProps {
  /**
   * The response row that was selected in the Responses panel.
   * Provides context (assetId, issueId if known, threadTitle).
   */
  responseRow: CampaignResponseRow;
  /**
   * The issue id to load. Obtained from the response row or by the host.
   * Required — the panel renders a loading/empty state when absent.
   */
  issueId: string;
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * CampaignThreadPanel
 *
 * Wraps the shared-ui `MessageThread` primitive to display the per-asset
 * campaign conversation thread. Loads the target Issue by id and binds
 * the compose input to `useCreateIssueMessage` (the same mutation used by
 * the standard Issue panel).
 *
 * Layout: MessageThread handles header-slot + message list + composer.
 * This panel passes no header slot (campaigns don't need severity / assignee
 * chrome) and keeps showComposer=true so OEM users can reply.
 */
export const CampaignThreadPanel = ({
  issueId,
}: CampaignThreadPanelProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const { issue, isLoading } = useIssue(issueId);
  const { createIssueMessage } = useCreateIssueMessage();
  const { data: inboxData } = useFindUnreadActivityInbox();
  const { setAsRead } = useSetActivityInboxRead();
  const messagesRef = useRef<ElementRef<typeof MessageThread>>(null);
  const queryClient = useQueryClient();

  const onPostMessage = async (message: NewMessage) => {
    if (!issue) return;
    await createIssueMessage({
      issue,
      message,
      options: {
        onSettled: async (data: Message | undefined) => {
          // onSettled fires on both success and failure; `data` is undefined
          // when the mutation rejects. Only inject a real message.
          if (data) messagesRef.current?.addNewMessage(data);

          await queryClient.invalidateQueries({
            queryKey: cacheKeys('issue').all,
          });
        },
      },
    });
  };

  if (isLoading) {
    return (
      <View style={styles.centeredMessage}>
        <Text style={styles.centeredMessageText}>
          {t('campaigns.thread.loading', 'Loading thread…')}
        </Text>
      </View>
    );
  }

  if (!issue) {
    return (
      <View style={styles.centeredMessage}>
        <Text style={styles.centeredMessageText}>
          {t('campaigns.thread.notFound', 'Thread not found')}
        </Text>
      </View>
    );
  }

  return (
    <MessageThread
      ref={messagesRef}
      issue={issue as DomainIssue}
      onPostMessage={onPostMessage}
      mediaPicker={MediaPicker}
      mediaUrl={getMediaBaseUrl()}
      inbox={inboxData?.items ?? []}
      setAsRead={setAsRead}
      workspaceId={workspace?.id}
      showComposer={true}
      placeholder={t('campaigns.thread.composePlaceholder', 'Reply…')}
      onMessagesCountChange={undefined}
    />
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  centeredMessage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing['2xl'],
  },
  centeredMessageText: {
    ...tokens.typography.body.small,
    color: Colors.role.textSecondary,
  },
});
